import React from 'react';
import { ArrowLeft, Lightbulb, Code, Monitor, Key, PhoneCall, ArrowRight } from 'lucide-react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import BackToTop from '../components/BackToTop';

const bgImage = 'https://customer-assets.emergentagent.com/job_3f9e40e3-cca9-4507-83e7-20ced098354a/artifacts/h7qxejo8_PAYMENT%20INTEGRATION%20ANIME.png';
const hostedPaymentScreenshot = 'https://customer-assets.emergentagent.com/job_3f9e40e3-cca9-4507-83e7-20ced098354a/artifacts/1t8kcla7_SCREENSHOT%20OF%20HOSTED%20PAYMENT.png';

const PaymentIntegration = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const fromTopNav = location.state?.from === 'top-nav';

  return (
    <div className="min-h-screen flex flex-col" data-testid="payment-integration-page">
      <Header />

      <div className="h-20" />

      {/* Full-page background image */}
      <div className="relative flex-1">
        <div
          className="fixed inset-0 top-20 bg-cover bg-center"
          style={{ backgroundImage: `url(${bgImage})` }}
        />
        <div className="fixed inset-0 top-20 bg-white/0" />

        {/* Content */}
        <div className="relative z-10 max-w-4xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-10">
          <button
            onClick={() => (fromTopNav ? navigate('/') : navigate(-1))}
            className="flex items-center gap-2 text-[#1F4E79] hover:text-[#163a5f] font-medium mb-8 transition-colors duration-200"
            data-testid="payment-int-back-button"
          >
            <ArrowLeft size={18} />
            {fromTopNav ? 'Back to Home' : 'Back'}
          </button>

          {/* Intro */}
          <div className="bg-white/80 backdrop-blur-sm rounded-lg shadow-lg p-8 sm:p-12 mb-8">
            <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-3" data-testid="payment-int-title">
              Payment Integration
            </h1>
            <p className="text-lg text-gray-600 font-bold mb-6">
              Flexible integration solutions designed for your business needs
            </p>

            <div className="space-y-4 text-gray-700 text-base sm:text-lg leading-relaxed">
              <p>
                eCOMM Merchant Solutions offers a comprehensive suite of integration options built on our proprietary technology platform. Whether you're a developer seeking full control or a business owner wanting simple implementation, our flexible solutions ensure quick, hassle-free integration that gets you accepting payments within days, not weeks.
              </p>
            </div>
          </div>

          {/* Integration Methods Header */}
          <div className="bg-white/80 backdrop-blur-sm rounded-lg shadow-lg p-8 sm:p-10 mb-6">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900" data-testid="integration-methods-title">
              Integration Methods
            </h2>
            <p className="text-gray-700 text-base sm:text-lg leading-relaxed mt-2 font-bold">
              Choose the approach that best fits your technical requirements and business model.
            </p>
          </div>

          {/* Direct API Integration */}
          <div className="bg-white/80 backdrop-blur-sm rounded-lg shadow-lg p-8 sm:p-10 mb-6">
            <h3 className="text-xl sm:text-2xl font-bold text-[#1F4E79] mb-4 flex items-center gap-2">
              <Code size={24} className="flex-shrink-0" />
              Direct API Integration
            </h3>
            <p className="text-gray-700 text-base leading-relaxed mb-4">
              Our Application Programming Interface (API) provides full integration capabilities, allowing you to seamlessly embed payment processing directly into your website or application. Customer card details are securely transmitted to our platform whilst maintaining your desired user experience.
            </p>
            <ul className="space-y-2 text-gray-700 text-base leading-relaxed mb-6 list-disc pl-6">
              <li>Technology-independent XML architecture</li>
              <li>Full customisation of the payment journey</li>
              <li>Complete control over user interface design</li>
            </ul>
            <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg p-4">
              <Lightbulb size={20} className="text-amber-500 flex-shrink-0 mt-0.5" />
              <p className="text-gray-800 text-sm font-medium">
                <strong>Best For:</strong> Businesses with development resources seeking maximum flexibility and brand control.
              </p>
            </div>
          </div>

          {/* Hosted Payment Page / iFrame */}
          <div className="bg-white/80 backdrop-blur-sm rounded-lg shadow-lg p-8 sm:p-10 mb-6">
            <h3 className="text-xl sm:text-2xl font-bold text-[#1F4E79] mb-4 flex items-center gap-2">
              <Monitor size={24} className="flex-shrink-0" />
              Hosted Payment Page / iFrame
            </h3>
            <p className="text-gray-700 text-base leading-relaxed mb-4">
              <strong>Hosted Payment Page:</strong> Redirect customers to our secure, branded payment environment when they're ready to pay. After completing their transaction, customers return seamlessly to your website.
            </p>
            <p className="text-gray-700 text-base leading-relaxed mb-4">
              <strong>iFrame Integration:</strong> Embed our secure payment form directly within your checkout page, maintaining your website's look and feel whilst benefiting from our security infrastructure.
            </p>
            <ul className="space-y-2 text-gray-700 text-base leading-relaxed mb-6 list-disc pl-6">
              <li>No sensitive data handling required</li>
              <li>Eliminates PCI compliance burden</li>
              <li>Rapid implementation</li>
              <li>Bank-level security standards</li>
            </ul>

            {/* Hosted Payment Screenshot */}
            <div className="mb-6 bg-gray-50 rounded-lg p-4 border border-gray-200">
              <p className="text-sm text-gray-500 font-medium mb-3">Example: Hosted Payment Page</p>
              <div className="flex justify-center">
                <img
                  src={hostedPaymentScreenshot}
                  alt="eCOMM Hosted Payment Page Example"
                  className="max-w-xs w-full rounded-lg shadow-md border border-gray-200"
                  data-testid="hosted-payment-screenshot"
                />
              </div>
            </div>

            <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg p-4">
              <Lightbulb size={20} className="text-amber-500 flex-shrink-0 mt-0.5" />
              <p className="text-gray-800 text-sm font-medium">
                <strong>Best For:</strong> Businesses wanting secure payment processing without extensive development resources. <Link to="/technical-documentation/api-reference/hosted-payment-page" state={{ from: '/payment-integration' }} className="text-[#1F4E79] underline hover:text-[#163a5f] font-semibold" data-testid="hosted-payment-learn-more">Learn more: Developer Guide for Hosted Payment Page</Link>
              </p>
            </div>
          </div>

          {/* Token Integration */}
          <div className="bg-white/80 backdrop-blur-sm rounded-lg shadow-lg p-8 sm:p-10 mb-6">
            <h3 className="text-xl sm:text-2xl font-bold text-[#1F4E79] mb-4 flex items-center gap-2">
              <Key size={24} className="flex-shrink-0" />
              Token Integration
            </h3>
            <p className="text-gray-700 text-base leading-relaxed mb-4">
              Our tokenisation system replaces sensitive card data with randomly generated tokens, enabling secure storage of customer payment information for future transactions.
            </p>
            <p className="text-gray-700 text-base leading-relaxed mb-3 font-semibold">Available methods include:</p>
            <div className="space-y-2 text-gray-700 text-base leading-relaxed mb-4 pl-2">
              <p><strong>Register by Token:</strong> Store customer details for future payments</p>
              <p><strong>Pay by Token:</strong> Process payments using previously stored tokens</p>
              <p><strong>Register and Pay by Token:</strong> Combined registration and payment in one process</p>
            </div>
            <ul className="space-y-2 text-gray-700 text-base leading-relaxed mb-6 list-disc pl-6">
              <li>Enhanced security through tokenisation</li>
              <li>Improved customer experience with saved payment methods</li>
              <li>Reduced PCI scope</li>
              <li>Support for recurring payments and subscriptions</li>
            </ul>
            <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg p-4">
              <Lightbulb size={20} className="text-amber-500 flex-shrink-0 mt-0.5" />
              <p className="text-gray-800 text-sm font-medium">
                <strong>Best For:</strong> Subscription businesses, repeat customers and merchants requiring stored payment methods. <Link to="/technical-documentation/api-reference/hosted-payment-token" state={{ from: '/payment-integration' }} className="text-[#1F4E79] underline hover:text-[#163a5f] font-semibold" data-testid="hosted-payment-token-learn-more">Learn more: Developer Guide for Token Integration</Link>
              </p>
            </div>
          </div>

          {/* Virtual Terminal */}
          <div className="bg-white/80 backdrop-blur-sm rounded-lg shadow-lg p-8 sm:p-10 mb-6">
            <h3 className="text-xl sm:text-2xl font-bold text-[#1F4E79] mb-1 flex items-center gap-2">
              <PhoneCall size={24} className="flex-shrink-0" />
              Virtual Terminal
            </h3>
            <p className="text-gray-500 text-sm font-medium mb-4">Zero Setup Solution for Phone and Mail Orders</p>
            <p className="text-gray-700 text-base leading-relaxed mb-4">
              Process payments directly through our secure back-office portal — perfect for Mail Order/Telephone Order (MOTO) transactions and businesses that don't require website integration.
            </p>
            <ul className="space-y-2 text-gray-700 text-base leading-relaxed mb-6 list-disc pl-6">
              <li>No technical setup required</li>
              <li>Instant access to payment processing</li>
              <li>Perfect for phone-based sales</li>
              <li>Comprehensive transaction management tools</li>
            </ul>
            <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg p-4">
              <Lightbulb size={20} className="text-amber-500 flex-shrink-0 mt-0.5" />
              <p className="text-gray-800 text-sm font-medium">
                <strong>Best For:</strong> Service providers, phone sales teams and businesses processing occasional card-not-present transactions.
              </p>
            </div>
          </div>

          {/* Getting Started */}
          <div className="bg-white/80 backdrop-blur-sm rounded-lg shadow-lg p-8 sm:p-10 mb-6">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4" data-testid="getting-started-title">
              Getting Started with eCOMM Merchant Solutions
            </h2>
            <p className="text-gray-700 text-base leading-relaxed mb-6">
              Streamlined onboarding across two key workstreams:
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div>
                <h4 className="text-lg font-bold text-[#1F4E79] mb-3">Commercial Onboarding</h4>
                <ul className="space-y-2 text-gray-700 text-base list-disc pl-6">
                  <li>Account setup and verification</li>
                  <li>Pricing and terms agreement</li>
                  <li>Compliance documentation</li>
                  <li>Access credentials provisioning</li>
                </ul>
              </div>
              <div>
                <h4 className="text-lg font-bold text-[#1F4E79] mb-3">Technical Integration</h4>
                <ul className="space-y-2 text-gray-700 text-base list-disc pl-6">
                  <li>Integration method selection</li>
                  <li>Development and testing</li>
                  <li>Security validation</li>
                  <li>Go-live support</li>
                </ul>
              </div>
            </div>

            <p className="text-gray-700 text-base leading-relaxed mt-6">
              For detailed technical specifications and implementation guides, visit our{' '}
              <Link to="/technical-documentation" className="text-[#1F4E79] font-semibold underline hover:text-[#163a5f] transition-colors duration-200">
                Technical Documentation
              </Link>.
            </p>
          </div>

          {/* Need Help Choosing */}
          <div className="bg-white/80 backdrop-blur-sm rounded-lg shadow-lg p-8 sm:p-12 mb-10">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4" data-testid="need-help-title">
              Need Help Choosing?
            </h2>
            <p className="text-gray-700 text-base sm:text-lg leading-relaxed mb-8">
              Our technical team is here to help you select the best integration method for your specific requirements. Contact us to discuss your needs and get expert recommendations tailored to your business.
            </p>

            <div className="space-y-4">
              <div>
                <p className="font-semibold text-gray-900">Phone Support</p>
                <p className="text-gray-700">EU: +353 (0)46 901 1288</p>
                <p className="text-gray-700">UK: +44 (0)20 7183 5253</p>
              </div>
              <div>
                <p className="font-semibold text-gray-900">Email</p>
                <a
                  href="mailto:csr@ecomm365.com"
                  className="text-[#1F4E79] hover:text-[#163a5f] underline transition-colors duration-200"
                  data-testid="payment-int-email-link"
                >
                  csr@ecomm365.com
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="relative z-10">
        <Footer />
      </div>
      <BackToTop />
    </div>
  );
};

export default PaymentIntegration;
